/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * W137 R3b — THE EXERCISE LABELS OF THE EXTENDED WEEK (the owner's word, 2026-10-02 ~11:40): *"Instead use before the
 * exercise titles, A1, B1 and so on. Supersets will show as A1, A2 and so on. And same for warm up and cool down like: W1
 * and C1 and so on."*
 *
 * ONE pure rule, executed by `exerciseLabels.test.ts`:
 *   · a WARM-UP section (by section TYPE `warm_up`, never by name): `W1`, `W2`, … counting across the workout's warm-ups;
 *   · a COOL-DOWN section (`cool_down`): `C1`, `C2`, …;
 *   · every other section: letters in workout order, CONTINUING across sections (never restarting per section) — a
 *     straight exercise takes the next letter with `1`; a SUPERSET's members share ONE letter and count (`C1`, `C2`, `C3`);
 *     after `Z` come `AA`, `AB`, … (bijective base 26; his days run to ~20, so the tail is for safety, not use).
 * Fable's resolution of the collision his scheme implies (a third main group is also `C…`): the main letters stay natural,
 * and a cool-down's `C` is told apart by its own section heading AND its ink (`kind` says which) — never colour alone.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

export type LabelKind = "warm" | "cool" | "main";
export type ExerciseLabel = { label: string; kind: LabelKind };

/** 0 → A, 25 → Z, 26 → AA, 27 → AB, … */
export function letterFor(index: number): string {
  let n = Math.max(0, Math.trunc(index)) + 1;
  let out = "";
  while (n > 0) {
    const r = (n - 1) % 26;
    out = String.fromCharCode(65 + r) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

export function exerciseLabels(sections: readonly {
  sectionType?: string | null;
  rows: readonly { exerciseRowId: string; supersetWithNext: boolean }[];
}[]): Map<string, ExerciseLabel> {
  const out = new Map<string, ExerciseLabel>();
  let warm = 0, cool = 0, letter = 0;
  for (const s of sections) {
    if (s.sectionType === "warm_up" || s.sectionType === "cool_down") {
      const kind: LabelKind = s.sectionType === "warm_up" ? "warm" : "cool";
      for (const r of s.rows) {
        if (kind === "warm") { warm += 1; out.set(r.exerciseRowId, { label: `W${String(warm)}`, kind }); }
        else { cool += 1; out.set(r.exerciseRowId, { label: `C${String(cool)}`, kind }); }
      }
      continue;
    }
    // a run: a row and every row its `superset_with_next` chain joins — inside ONE section (the boundary flag's rule)
    let i = 0;
    while (i < s.rows.length) {
      let j = i;
      while (j < s.rows.length - 1 && s.rows[j]!.supersetWithNext) j += 1;
      const l = letterFor(letter);
      for (let k = i; k <= j; k++) out.set(s.rows[k]!.exerciseRowId, { label: `${l}${String(k - i + 1)}`, kind: "main" });
      letter += 1;
      i = j + 1;
    }
  }
  return out;
}
